import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import api from "@/lib/api";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data?.message || "Subscribed!");
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="newsletter" className="py-20 sm:py-24 lg:py-32" data-testid="newsletter-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden bg-brand text-white rounded-2xl sm:rounded-[2rem] px-6 py-12 sm:px-10 sm:py-16 lg:px-14 lg:py-20">
          <svg className="absolute inset-0 w-full h-full opacity-[0.08]" viewBox="0 0 800 400" preserveAspectRatio="none" aria-hidden="true">
            <g fill="none" stroke="white" strokeWidth="1">
              {Array.from({ length: 14 }).map((_, i) => (
                <path key={i} d={`M0 ${60 + i * 22} C 200 ${30 + i * 22}, 600 ${100 + i * 22}, 800 ${60 + i * 22}`} />
              ))}
            </g>
          </svg>

          <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-soft/30 text-amber-soft text-[11px] sm:text-xs font-medium">
                <Mail className="w-3.5 h-3.5" /> Mondays · 1 essay · zero noise
              </div>
              <h2 className="mt-4 sm:mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                The journal, <span className="italic text-amber-soft">in your inbox.</span>
              </h2>
              <p className="mt-3 sm:mt-4 text-white/75 text-base sm:text-lg leading-relaxed max-w-xl">
                One short essay every Monday. Mindmaps, books, learning. The kind of email you actually want to open.
              </p>
            </div>

            <div className="md:col-span-5">
              {subscribed ? (
                <div data-testid="newsletter-success" className="bg-white/10 border border-white/20 rounded-2xl px-6 py-7">
                  <p className="font-serif text-xl font-semibold">You're on the list.</p>
                  <p className="mt-2 text-white/75 text-sm leading-relaxed">
                    One weekly digest of new essays, every Monday. Meanwhile, if you want to try mapping a book yourself:
                  </p>
                  <a
                    href="https://summarymaps.com/?utm_source=journal&utm_medium=newsletter"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 bg-amber-soft text-brand font-medium rounded-full px-6 py-3 hover:bg-amber-softer transition-colors"
                    data-testid="newsletter-trial-cta"
                  >
                    Try Summary Maps free for 7 days <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ) : (
              <form onSubmit={onSubmit} className="space-y-3" data-testid="newsletter-form">
                <label htmlFor="newsletter-email" className="sr-only">Email</label>
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
                  <input
                    id="newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@reader.com"
                    className="flex-1 min-w-0 bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-full px-5 py-3.5 outline-none focus:ring-2 focus:ring-amber-soft focus:border-transparent transition"
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 bg-amber-soft text-brand font-medium rounded-full px-6 py-3.5 hover:bg-amber-softer transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid="newsletter-submit-btn"
                  >
                    {submitting ? "Subscribing…" : (<>Subscribe <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </div>
                <p className="text-xs text-white/55">No spam. Unsubscribe anytime.</p>
              </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
